// Contracts for «Mahjong Tables» (mahjong-table, 广东麻将 推倒胡): four seats, bots fill empty seats,
// one bet per hand for every human at the table. Turn-based: every player gets only their own view.
//   GET /api/mahjong-table/state → MjtTableView | null
//   GET /api/mahjong-table/lobby → MjtLobbyResponse
//   socket: mjt:queue / mjt:leave / mjt:action / mjt:auto / mjt:resume / mjt:present, pushes mjt:table / mjt:queue
// «Connected» at a table = at least one socket of the player has the table page open (`mjt:resume` / `mjt:present(true)`
// on the page; `mjt:present(false)` or the socket's disconnect when leaving it). Pushes go to every socket of the user.
import { z } from 'zod';
import type { ApiError } from '../errors';

/** Tables by fixed stake (lucky numbers, no «4»). */
export const MJT_STAKES = [18, 88, 888] as const;
export type MjtStake = (typeof MJT_STAKES)[number];
export const zMjtStake = z.union([z.literal(18), z.literal(88), z.literal(888)]);
/** 0 = East (dealer of the hand), then South, West, North */
export type Seat = 0 | 1 | 2 | 3;

export type MjtAction =
  | { type: 'discard'; tile: number } // physical tile id 0…135
  | { type: 'tsumo' } // 自摸
  | { type: 'kong'; kind: number } // concealed or added kong (tile kind 0…33)
  | { type: 'ron' }
  | { type: 'pong' }
  | { type: 'claimKong' }
  | { type: 'pass' }; // answers to a discard

const zTileId = z.number().int().min(0).max(135);
const zTileKind = z.number().int().min(0).max(33);

export const zMjtAction: z.ZodType<MjtAction> = z.discriminatedUnion('type', [
  z.object({ type: z.literal('discard'), tile: zTileId }).strict(),
  z.object({ type: z.literal('tsumo') }).strict(),
  z.object({ type: z.literal('kong'), kind: zTileKind }).strict(),
  z.object({ type: z.literal('ron') }).strict(),
  z.object({ type: z.literal('pong') }).strict(),
  z.object({ type: z.literal('claimKong') }).strict(),
  z.object({ type: z.literal('pass') }).strict(),
]);
/** `mjt:action` payload; `seq` = table sequence the client acted on (idempotency / stale guard). */
export const zMjtActionInput = z.object({ handId: z.string().uuid(), seq: z.number().int().min(0), action: zMjtAction }).strict();
export type MjtActionInput = z.infer<typeof zMjtActionInput>;

export type MjtMeld = { type: 'pong' | 'kong' | 'concealedKong'; tiles: number[]; from: Seat | null };
export interface MjtSeatView {
  seat: Seat;
  player: { id: string; nickname: string; avatar: string | null; bot: boolean } | null;
  handCount: number;
  melds: MjtMeld[];
  discards: number[];
  auto: boolean;
  connected: boolean;
}
export type MjtPhase = 'waiting' | 'turn' | 'claims' | 'ended';
export interface MjtHandResult {
  winners: { seat: Seat; fan: number; hand: number[]; melds: MjtMeld[]; payout: number }[];
  discarder: Seat | null;
  selfDraw: boolean;
  exhausted: boolean;
  bank: number;
  fee: number;
  /** a bot won or the hand was exhausted (流局) → humans' stakes are returned */
  refunded: boolean;
}
/** Header of a tournament table (sub-project 8): hands are played for points, without bets. */
export interface MjtTourneyView {
  tourneyId: string;
  title: string;
  stage: number;
  /** 1-based number of the current hand in the stage */
  handNo: number;
  /** hands in the stage */
  hands: number;
  /** stage points by seat */
  points: number[];
}
/**
 * 练习 — a training table: the player alone with three bots, no stake (no Bet, no ledger, no XP / achievements /
 * leaderboards); the points (the tournament scoring, Σ = 0) are for display only. Leaving closes the table.
 */
export interface MjtTrainingView {
  /** running points by seat (Σ = 0) */
  points: number[];
  /** hands finished at this table */
  hands: number;
}
export interface MjtTableView {
  tableId: string;
  /** null = tournament or training table (no bets) */
  stake: MjtStake | null;
  /** set on tournament tables */
  tourney?: MjtTourneyView | null;
  /** set on training tables (练习) */
  training?: MjtTrainingView | null;
  handId: string | null;
  roundNo: number | null;
  serverSeedHash: string | null;
  serverSeed: string | null;
  clientSeed: string | null;
  phase: MjtPhase;
  seq: number;
  /** always 4 */
  seats: MjtSeatView[];
  mySeat: Seat | null;
  myHand: number[] | null;
  myLegal: MjtAction[];
  current: Seat | null;
  lastDiscard: { seat: Seat; tile: number } | null;
  wallLeft: number;
  deadline: number | null;
  serverNow: number;
  result: MjtHandResult | null;
  /** cash table between hands: when the next hand is dealt (its stakes taken); null / absent otherwise */
  nextAt?: number | null;
  /**
   * cash table, own seat: true = stands up when the next hand is dealt — asked to («Stand up»), left the page, or
   * idle in this hand (托管, or only timeouts, no own move) without confirming «Next hand» (`mjt:next`):
   * a player who is not there is never staked again
   */
  myLeaving?: boolean;
  /** cash table, own seat: idle in this hand (托管 / only timeouts) — stays only after «Next hand» */
  myIdle?: boolean;
}
/** botsIn — ms until bots take the empty seats (null = nobody waiting) */
export interface MjtQueueView { stake: MjtStake; queued: number; tables: number; botsIn: number | null }
export interface MjtLobbyResponse { stakes: MjtQueueView[] }

export interface MjtServerToClient { 'mjt:table': (v: MjtTableView) => void; 'mjt:queue': (v: MjtQueueView) => void }
export interface MjtClientToServer {
  'mjt:queue': (stake: MjtStake, ack: (r: MjtQueueView | MjtTableView | ApiError) => void) => void;
  /** 练习: a training table — the player and three bots, no stake, dealt at once (seated already → that table) */
  'mjt:training': (ack: (r: MjtTableView | ApiError) => void) => void;
  'mjt:leave': (ack: (r: { ok: true } | ApiError) => void) => void;
  /**
   * «Next hand» at a cash table: stays for the next deal (takes back a stand-up, switches 托管 off) — an idle player
   * (托管 / only timeouts in the hand) stands up at the deal without it
   */
  'mjt:next': (ack: (r: MjtTableView | ApiError) => void) => void;
  'mjt:action': (input: { handId: string; seq: number; action: MjtAction }, ack: (r: MjtTableView | ApiError) => void) => void;
  /**
   * 托管 on / off. `'leave'` — switched on by the page itself when the player leaves it (switched off on return); only
   * 托管 switched by the player carries over to the next stage's table of a tournament
   */
  'mjt:auto': (on: boolean, reason?: 'leave') => void;
  'mjt:resume': (ack: (r: MjtTableView | null | ApiError) => void) => void;
  /** the table page is open on this socket (true) / was left (false); `mjt:resume`, `mjt:queue`, `mjt:action` imply true */
  'mjt:present': (on: boolean) => void;
}
